'use client'

import { MapPin, Brain, Cloud, Shield } from 'lucide-react'
import { useEffect, useState } from 'react'

interface BetaDisclaimerProps {
  variant?: 'banner' | 'modal' | 'inline'
  onDismiss?: () => void
}

export function BetaDisclaimer({
  variant = 'banner',
  onDismiss,
}: BetaDisclaimerProps) {
  const [isDismissed, setIsDismissed] = useState(false)
  const [installBannerVisible, setInstallBannerVisible] = useState(false)
  const [installVisibilityReady, setInstallVisibilityReady] = useState(false)
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)

  useEffect(() => {
    if (variant !== 'banner') return
    if (typeof window === 'undefined') return
    const dismissed = localStorage.getItem('wagamour_beta_dismissed')
    if (dismissed === 'true') setIsDismissed(true)
  }, [variant])

  useEffect(() => {
    if (variant !== 'banner') return
    if (typeof window === 'undefined') return

    // Prefer explicit visibility signal from install banner.
    let visibleFromSignal: boolean | null = null
    try {
      const v = sessionStorage.getItem('wagamour_install_banner_visible')
      if (v === 'true') visibleFromSignal = true
      if (v === 'false') visibleFromSignal = false
    } catch {
      // ignore
    }

    // Fallback heuristic for first paint before signal is set.
    if (visibleFromSignal == null) {
      const dismissedTsRaw = localStorage.getItem('pwa-install-dismissed')
      const dismissedTs = dismissedTsRaw ? parseInt(dismissedTsRaw, 10) : NaN
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000
      const installDismissedRecently = Number.isFinite(dismissedTs) && dismissedTs > oneDayAgo

      const ua = navigator.userAgent
      const vendor = navigator.vendor || ''
      const isIOSDevice =
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      const isChrome = /Chrome/.test(ua) && /Google Inc/.test(vendor)
      const isAndroid = /Android/.test(ua)
      const isChromeAndroid = isChrome && isAndroid && !isIOSDevice
      const isChromeDesktop = isChrome && !/Android|Mobile/.test(ua)
      const isStandalone =
        window.matchMedia?.('(display-mode: standalone)').matches === true ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true

      visibleFromSignal =
        !isStandalone &&
        !installDismissedRecently &&
        (isIOSDevice || isChromeAndroid || isChromeDesktop)
    }

    setInstallBannerVisible(Boolean(visibleFromSignal))
    setInstallVisibilityReady(true)

    const onInstallBannerVisibility = (event: Event) => {
      const detail = (event as CustomEvent<boolean>).detail
      setInstallBannerVisible(Boolean(detail))
      setInstallVisibilityReady(true)
    }

    window.addEventListener(
      'wagamour:install-banner-visibility',
      onInstallBannerVisibility as EventListener
    )
    return () => {
      window.removeEventListener(
        'wagamour:install-banner-visibility',
        onInstallBannerVisibility as EventListener
      )
    }
  }, [variant])

  if (variant === 'inline') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
        <div className="flex items-start gap-3">
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900 mb-1">Important information</p>
            <p className="text-amber-800">
              Wagamour provides AI-powered guidance based on available signals.
              Always use your judgment and consult a professional for medical,
              safety, or emergency concerns.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (variant === 'modal') {
    return (
      <div className="bg-white rounded-xl shadow-xl max-w-2xl mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            Product disclaimer
          </h2>
        </div>

        <div className="space-y-4 text-sm text-gray-700">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="font-semibold text-amber-900 mb-2">
              ⚠️ Use at Your Own Risk
            </p>
            <p className="text-amber-800">
              Wagamour provides AI-powered guidance based on available signals.
              Always use your judgment and consult a professional for medical,
              safety, or emergency concerns. While we strive for accuracy, some
              information may be incomplete or subject to change.
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 mb-1">
                  Location Data
                </p>
                <p className="text-blue-800 text-xs">
                  Location-based features rely on GPS and third-party mapping
                  services. Accuracy may vary based on device capabilities,
                  signal strength, and service availability. Always verify park
                  locations and conditions before visiting.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
              <Brain className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-purple-900 mb-1">
                  AI-Generated Content
                </p>
                <p className="text-purple-800 text-xs">
                  Comfort-related recommendations and in-app suggestions are
                  generated using models and rules. They are not professional
                  medical, legal, or safety advice. Always consult a
                  veterinarian for medical concerns and use your own judgment
                  outdoors.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-sky-50 rounded-lg">
              <Cloud className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sky-900 mb-1">
                  Weather Information
                </p>
                <p className="text-sky-800 text-xs">
                  Weather data is provided by third-party services and may not
                  always be current or accurate. Weather conditions can change
                  rapidly. Always check current conditions before outdoor
                  activities with your pet.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Shield className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900 mb-1">
                  Data & Privacy
                </p>
                <p className="text-gray-800 text-xs">
                  We may collect usage data to improve Wagamour and enhance
                  features. Your privacy is important to us. See our Privacy
                  Policy for details on data collection and usage.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <p className="text-xs text-gray-600">
              By using Wagamour, you acknowledge that you have read and
              understood this disclaimer and agree to use the application at
              your own risk. We are not liable for any decisions made based on
              information provided by this application.
            </p>
          </div>
        </div>

        {onDismiss && (
          <button
            onClick={onDismiss}
            className="mt-6 w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            I Understand
          </button>
        )}
      </div>
    )
  }

  // Banner variant (default)
  if (isDismissed) return null
  if (!installVisibilityReady) return null
  if (installBannerVisible) return null

  return (
    <>
      <div className="bg-transparent">
        <div className="container-mobile pt-2">
          <div className="flex h-10 items-center justify-between gap-2 rounded-xl border border-slate-200/70 bg-white/75 px-3 py-1.5 shadow-sm backdrop-blur-sm">
            <p className="min-w-0 truncate whitespace-nowrap text-[13px] font-medium text-slate-700">
              Powered by live location, AI, and real-time data
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setShowLearnMoreModal(true)}
                className="rounded-md px-1.5 py-1 text-[13px] font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                Learn more
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsDismissed(true)
                  if (typeof window !== 'undefined') {
                    localStorage.setItem('wagamour_beta_dismissed', 'true')
                  }
                  onDismiss?.()
                }}
                className="rounded-md px-1.5 py-1 text-[13px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      </div>
      {showLearnMoreModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/40 px-4 py-6">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-slate-900">How Wagamour works</h3>
              <button
                type="button"
                onClick={() => setShowLearnMoreModal(false)}
                className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close details"
              >
                ×
              </button>
            </div>
            <div className="space-y-3 text-sm text-slate-700">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="mb-1 text-sm font-semibold text-slate-900">Location accuracy</p>
                <p className="text-xs leading-relaxed">
                  GPS accuracy may vary. Please confirm park details before visiting.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="mb-1 text-sm font-semibold text-slate-900">AI recommendations</p>
                <p className="text-xs leading-relaxed">
                  Suggestions are generated from available data and are designed to guide, not replace professional
                  judgment.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="mb-1 text-sm font-semibold text-slate-900">Weather data</p>
                <p className="text-xs leading-relaxed">
                  Conditions are sourced from third-party services and may change quickly.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
